import React, { useEffect } from 'react';
import { ConfigProvider } from 'antd';
import { getProjectTheme } from './helpers/getProjectTheme';
import { MainContext } from './store/MainContext';
import { CountriesContext } from './store/CountriesContext';
import { MainState } from './models/main-state';
import { initialMainState } from './assets/config/initial-main-state';
import { BrowserRouter } from 'react-router-dom';
import Views from './routes/Views';
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import { getUserLanguageInCookie, setUserLanguageInCookie } from './helpers/cookiesHandler';
import { englishPhrases } from './languages/englishPhrases';
import { arabicPhrases } from './languages/arabicPhrases';
import { frenchPhrases } from './languages/frenchPhrases';
import { polishPhrases } from './languages/polishPhrases';
import { initialCountriesState } from './assets/config/initial-countries-state';
import { CountriesState } from './models/countries-state';
import { DirectionContext } from './store/DirectionContext';
import { initialDirection } from './assets/config/initial-direction-state';
import { OrderLifeCycleContext } from './store/OrderLifeCycleContext';
import { MemoizedAxiosWrapper } from './assets/api/axiosInterceptor';
import { Configuration, ConfigurationContext, initialConfiguration } from './store/configurationContext';
import axios from 'axios';
import { API_ENDPOINTS } from './assets/api/endpoints';
import { getUserInfo } from './helpers/localStorageHandler';
import { AuthConfigurations } from './store/AuthConfigurationsContext';

function App() {
	const [mainState, setMainState] = React.useState<MainState>(initialMainState);
	const [countriesState, setCountriesState] = React.useState<CountriesState>(initialCountriesState);
	const [directionState, setDirectionState] = React.useState<'ltr' | 'rtl' | undefined>(initialDirection);
	const [newAssignee, setNewAssignee] = React.useState<string>('');
	const [configurationState, setConfigurationState] = React.useState<Configuration[]>(initialConfiguration);
	i18n.use(initReactI18next).init({
		resources: {
			en: { translation: englishPhrases },
			ar: { translation: arabicPhrases },
			fr: { translation: frenchPhrases },
			pl: { translation: polishPhrases },
		},
		lng: getUserLanguageInCookie(),
		fallbackLng: ['en', 'ar', 'pl', 'fr'].includes(navigator.language) ? navigator.language : 'en',
		interpolation: { escapeValue: false },
	});

	useEffect(() => {
		let lang = getUserLanguageInCookie();
		const body = document.body;
		if (lang === 'ar') {
			body.classList.remove('ltr');
			body.classList.add('rtl');
		} else {
			body.classList.remove('rtl');
			body.classList.add('ltr');
		}
		setDirectionState(lang === 'ar' ? 'rtl' : 'ltr');
	}, []);

	useEffect(() => {
		axios
			.post(API_ENDPOINTS.getGeneralConfig, {
				keys: [
					'Company_Name',
					'tab_logo',
					'header_image',
					'company_logo',
					'theme_color',
					'company_website',
					'session_expired_url',
					'background_image',
					'allowed_admins_emails',
					'recaptchaVersion',
					'isRecaptchaEnabled',
					'withdraw_required_live_picture',
					'default_language',
					'secondary_color',
					'request_action_otp_enabled',
					'hide_header_text',
				],
			})
			.then((response) => {
				const data = response.data.filter((item: Configuration) => item.configKey === 'secondary_color');
				if (data.length > 0) {
					setConfigurationState(response.data);
				} else {
					setConfigurationState([
						...response.data,
						{
							configId: 13,
							configKey: 'secondary_color',
							configValue: 'white',
							isPublic: true,
							value: 'white',
						},
					]);
				}
			})
			.catch((err) => console.error(err));
	}, []);

	return (
		<ConfigProvider theme={getProjectTheme()} direction={directionState}>
			<CountriesContext.Provider value={{ countriesState, setCountriesState }}>
				<MainContext.Provider value={{ mainState, setMainState }}>
					<DirectionContext.Provider value={{ directionState, setDirectionState }}>
						<OrderLifeCycleContext.Provider value={{ newAssignee, setNewAssignee }}>
							<ConfigurationContext.Provider value={{ configurationState, setConfigurationState }}>
								<BrowserRouter>
									<MemoizedAxiosWrapper>
										<AuthConfigurations>
											<Views />
										</AuthConfigurations>
									</MemoizedAxiosWrapper>
								</BrowserRouter>
							</ConfigurationContext.Provider>
						</OrderLifeCycleContext.Provider>
					</DirectionContext.Provider>
				</MainContext.Provider>
			</CountriesContext.Provider>
		</ConfigProvider>
	);
}

export default App;
